import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faXmark } from "@fortawesome/free-solid-svg-icons";

type QuizQuestion = {
  id: string;
  type: "mcq" | "boolean";
  question: string;
  options: string[];
  correctIndex: number;
  explanation: string;
  imageUrl?: string;
};

export type QuizPayload = {
  mode: "quiz";
  title: string;
  topic: string;
  numQuestions: number;
  questions: QuizQuestion[];
};

type Props = {
  quiz: QuizPayload;
  isDarkMode: boolean;
};

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

// Generate a stable key for sessionStorage based on quiz content
const getSessionKey = (quiz: QuizPayload) => {
  const base = `quiz_${quiz.topic || quiz.title || 'unknown'}_${quiz.numQuestions || 0}`;
  return base.replace(/\s+/g, '_').toLowerCase();
};

export const QuizCardStack = ({ quiz, isDarkMode }: Props) => {
  const totalQuestions = clamp(Number(quiz?.numQuestions) || quiz?.questions?.length || 0, 0, 999);

  const initialQueue = useMemo(() => {
    const list = Array.isArray(quiz?.questions) ? quiz.questions : [];
    return list.map((q, idx) => ({
      ...q,
      id: q.id || `q${idx + 1}`,
      options: Array.isArray(q.options) ? q.options : [],
      correctIndex: Number.isFinite(q.correctIndex) ? q.correctIndex : 0,
      explanation: typeof q.explanation === "string" ? q.explanation : "",
      imageUrl: typeof q.imageUrl === "string" ? q.imageUrl : undefined,
    }));
  }, [quiz]);

  const sessionKey = useMemo(() => getSessionKey(quiz), [quiz]);

  // Load saved session state
  const loadSessionState = useCallback(() => {
    try {
      const saved = sessionStorage.getItem(sessionKey);
      if (saved) {
        const state = JSON.parse(saved);
        return state;
      }
    } catch { }
    return null;
  }, [sessionKey]);

  const savedState = useMemo(() => loadSessionState(), [loadSessionState]);

  const [queue, setQueue] = useState(() => savedState?.queue || initialQueue);
  const [correctScore, setCorrectScore] = useState(() => savedState?.correctScore || 0);
  const [attempted, setAttempted] = useState<Record<string, boolean>>(() => savedState?.attempted || {});
  const [locked, setLocked] = useState<Record<string, boolean>>(() => savedState?.locked || {});
  const [selected, setSelected] = useState<Record<string, number | null>>(() => savedState?.selected || {});

  // Track if the auto-advance timer is active so manual nav cancels it
  const autoAdvanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Save state to sessionStorage whenever it changes
  useEffect(() => {
    try {
      const state = { queue, correctScore, attempted, locked, selected };
      sessionStorage.setItem(sessionKey, JSON.stringify(state));
    } catch { }
  }, [queue, correctScore, attempted, locked, selected, sessionKey]);

  useEffect(() => {
    // Only reset if the quiz content actually changed (new quiz)
    const existingState = loadSessionState();
    if (!existingState) {
      setQueue(initialQueue);
      setCorrectScore(0);
      setAttempted({});
      setLocked({});
      setSelected({});
    }
  }, [initialQueue]);

  const visibleCards = useMemo(() => queue.slice(0, 3), [queue]);
  const visibleCardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [stackMinHeight, setStackMinHeight] = useState<number>(420);
  const answeredCount = useMemo(() => Object.keys(attempted).length, [attempted]);

  const progressPercent = useMemo(() => {
    if (initialQueue.length === 0) return 0;
    return Math.round((answeredCount / initialQueue.length) * 100);
  }, [answeredCount, initialQueue.length]);

  // Circular progress bar SVG dimensions
  const circleSize = 40;
  const circleStrokeWidth = 4;
  const circleRadius = (circleSize - circleStrokeWidth) / 2;
  const circleCircumference = 2 * Math.PI * circleRadius;
  const strokeDashoffset = circleCircumference - (progressPercent / 100) * circleCircumference;

  const cancelAutoAdvance = useCallback(() => {
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
  }, []);

  const handleNext = useCallback(() => {
    cancelAutoAdvance();
    setQueue((prev) => {
      if (prev.length <= 1) return prev;
      const [first, ...rest] = prev;
      if (!first) return prev;
      return [...rest, first];
    });
  }, [cancelAutoAdvance]);

  const handlePrev = useCallback(() => {
    cancelAutoAdvance();
    setQueue((prev) => {
      if (prev.length <= 1) return prev;
      const last = prev[prev.length - 1];
      if (!last) return prev;
      return [last, ...prev.slice(0, -1)];
    });
  }, [cancelAutoAdvance]);

  useEffect(() => {
    const measure = () => {
      const heights = visibleCards
        .map((c) => visibleCardRefs.current[c.id]?.getBoundingClientRect().height || 0)
        .filter((h) => h > 0);

      const maxH = heights.length ? Math.max(...heights) : 0;
      const padding = 56;
      const next = Math.max(360, Math.ceil(maxH + padding));
      setStackMinHeight(next);
    };

    const raf = window.requestAnimationFrame(measure);
    const t = window.setTimeout(measure, 60);
    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(t);
    };
  }, [visibleCards]);

  // Just rotate to next card (cards are never removed from queue)
  const rotateToNext = useCallback(() => {
    setQueue((prev) => {
      if (prev.length <= 1) return prev;
      const [first, ...rest] = prev;
      if (!first) return prev;
      return [...rest, first];
    });
  }, []);

  // Image error tracking
  const [failedImages, setFailedImages] = useState<Set<string>>(() => new Set());

  // Cleanup auto-advance timer on unmount
  useEffect(() => {
    return () => cancelAutoAdvance();
  }, [cancelAutoAdvance]);

  const Card = ({ q, idx }: { q: QuizQuestion; idx: number }) => {
    const z = 10 - idx;
    const scale = 1 - idx * 0.05;
    const translateY = idx * 20;
    const dim = idx > 0;

    const cardRef = useRef<HTMLDivElement | null>(null);
    const startRef = useRef<{ x: number; y: number } | null>(null);
    const dragRef = useRef<{ x: number; y: number; allow: boolean; axis: boolean }>(
      { x: 0, y: 0, allow: false, axis: false }
    );

    const isTop = idx === 0;
    const isLocked = Boolean(locked[q.id]);

    const onPointerDown = (e: React.PointerEvent) => {
      if (!isTop) return;
      if (isLocked) return;
      const target = e.target as HTMLElement;
      if (target.closest("button")) return;

      const el = cardRef.current;
      if (!el) return;

      startRef.current = { x: e.clientX, y: e.clientY };
      dragRef.current = { x: 0, y: 0, allow: false, axis: false };
      el.setPointerCapture(e.pointerId);
      el.style.transition = "none";
    };

    const onPointerMove = (e: React.PointerEvent) => {
      if (!isTop) return;
      if (!startRef.current) return;
      if (isLocked) return;

      const el = cardRef.current;
      if (!el) return;

      const dx = e.clientX - startRef.current.x;
      const dy = e.clientY - startRef.current.y;
      dragRef.current.x = dx;
      dragRef.current.y = dy;

      if (!dragRef.current.axis) {
        if (Math.abs(dx) > 8) {
          dragRef.current.axis = true;
          dragRef.current.allow = true;
        } else if (Math.abs(dy) > 8) {
          startRef.current = null;
          el.style.transition = "transform 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.1)";
          el.style.transform = `translateY(${translateY}px) scale(${scale})`;
          return;
        }
        return;
      }

      if (dragRef.current.allow) {
        const rotate = dx * 0.04;
        el.style.transform = `translate3d(${dx}px, ${dy}px, 0) rotate(${rotate}deg)`;
      }
    };

    const onPointerUp = () => {
      if (!isTop) return;
      const el = cardRef.current;
      if (!el) return;

      const dx = dragRef.current.x;
      const dy = dragRef.current.y;
      startRef.current = null;

      const threshold = 90;
      if (dragRef.current.allow && Math.abs(dx) > threshold) {
        const direction = dx > 0 ? 1 : -1;
        // Smoothly snap back then rotate to next
        el.style.transition = "transform 0.35s ease-out";
        el.style.transform = `translateY(${translateY}px) scale(${scale})`;
        window.setTimeout(() => {
          rotateToNext();
        }, 350);
      } else {
        el.style.transition = "transform 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.1)";
        el.style.transform = `translateY(${translateY}px) scale(${scale})`;
      }

      dragRef.current = { x: 0, y: 0, allow: false, axis: false };
    };

    const letters = useMemo(() => {
      return q.options.map((_, i) => String.fromCharCode(65 + i));
    }, [q.options]);

    const onPick = (optIndex: number) => {
      if (isLocked) return;
      setSelected((prev) => ({ ...prev, [q.id]: optIndex }));

      setAttempted((prev) => {
        if (prev[q.id]) return prev;
        const isCorrect = optIndex === q.correctIndex;
        if (isCorrect) {
          setCorrectScore((s) => s + 1);
        }
        return { ...prev, [q.id]: true };
      });

      if (optIndex === q.correctIndex) {
        setLocked((prev) => ({ ...prev, [q.id]: true }));
        // Wait 4 seconds for the user to read the explanation, then smoothly auto-advance
        autoAdvanceTimerRef.current = setTimeout(() => {
          autoAdvanceTimerRef.current = null;
          rotateToNext();
        }, 4000);
      }
    };

    const showImage = q.imageUrl && !failedImages.has(q.imageUrl);

    return (
      <div
        ref={(el) => {
          cardRef.current = el;
          visibleCardRefs.current[q.id] = el;
        }}
        className={
          `absolute inset-x-3 sm:inset-x-4 mx-auto max-w-[600px] h-[max-content] rounded-2xl shadow-[0_15px_35px_rgba(0,0,0,0.08)] ` +
          `px-6 py-7 flex flex-col select-none touch-pan-y ` +
          `${isDarkMode ? "bg-[#2d2d2d] text-white" : "bg-white text-[#202124]"}`
        }
        style={{
          zIndex: z,
          transform: `translateY(${translateY}px) scale(${scale})`,
          opacity: idx > 1 ? 0 : 1,
          filter: dim ? `brightness(${1 - idx * 0.12})` : "brightness(1)",
          maxHeight: 520,
          willChange: "transform",
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        <div className="font-serif text-[0.98rem] xs:text-[1.04rem] sm:text-[1.1rem] md:text-[1.16rem] leading-snug mb-2 max-h-[220px] overflow-y-auto pr-2">
          {q.question}
        </div>

        {/* Quiz question image */}
        {showImage && (
          <div className="mb-3 mt-1 rounded-xl overflow-hidden">
            <img
              src={q.imageUrl!}
              alt="Question illustration"
              className="w-full max-h-[200px] object-contain rounded-xl cursor-pointer transition-transform duration-200 hover:scale-[1.01]"
              onClick={() => {
                try {
                  window.dispatchEvent(
                    new CustomEvent("openFullscreenImageModal", {
                      detail: {
                        images: [{ url: q.imageUrl!, description: "Quiz question image" }],
                        initialIndex: 0,
                      },
                    })
                  );
                } catch { }
              }}
              onError={() => {
                setFailedImages((prev) => new Set(prev).add(q.imageUrl!));
              }}
            />
          </div>
        )}

        <div className="flex flex-col gap-2 mt-2">
          {q.options.map((opt, i) => {
            const picked = selected[q.id] === i;
            const hasPicked = selected[q.id] != null;
            const isCorrect = i === q.correctIndex;
            const showCorrect = isLocked && isCorrect;
            const showIncorrect = hasPicked && picked && !isCorrect;

            const disablePick = isLocked;

            const base =
              "w-full flex items-center gap-4 px-4 py-3 text-left rounded-xl transition-all duration-200";

            const stateCls = showCorrect
              ? (isDarkMode ? "bg-[#1e3626]" : "bg-[#e6f4ea]")
              : showIncorrect
                ? (isDarkMode ? "bg-[#3d2121]" : "bg-[#fce8e6]")
                : isDarkMode
                  ? "bg-[#38383800] hover:bg-[#424242]"
                  : "bg-[#f8f9fa00] hover:bg-[#eef0f2]";

            const letterBoxBase =
              "w-[26px] h-[26px] rounded-md flex items-center justify-center text-xs font-semibold flex-shrink-0";
            const letterBox = showCorrect
              ? "bg-transparent text-[#5bb974]"
              : showIncorrect
                ? "bg-transparent text-[#e25c5a]"
                : isDarkMode
                  ? "bg-[#3c4043] text-[#e8eaed]"
                  : "bg-[#f1f3f4] text-[#5f6368]";

            const textCls = showCorrect
              ? (isDarkMode ? "text-[#5bb974] font-semibold" : "text-[#137333] font-semibold")
              : showIncorrect
                ? (isDarkMode ? "text-[#e25c5a] font-semibold" : "text-[#c5221f] font-semibold")
                : isDarkMode
                  ? "text-white"
                  : "text-[#202124]";

            return (
              <button
                key={`${q.id}-${i}`}
                type="button"
                onClick={() => onPick(i)}
                disabled={disablePick}
                className={`${base} ${stateCls} ${disablePick ? "opacity-80 cursor-not-allowed" : "cursor-pointer"}`}
              >
                <div className={`${letterBoxBase} ${letterBox}`}>
                  {showCorrect ? (
                    <FontAwesomeIcon icon={faCheck} className="w-3.5 h-3.5" />
                  ) : showIncorrect ? (
                    <FontAwesomeIcon icon={faXmark} className="w-3.5 h-3.5" />
                  ) : letters[i]}
                </div>
                <span className={`text-[0.95rem] xs:text-[1rem] sm:text-[1.02rem] ${textCls}`}>{opt}</span>
              </button>
            );
          })}
        </div>

        {isLocked && (
          <div className={
            `mt-4 text-sm rounded-xl px-4 py-3 ` +
            `${isDarkMode ? "bg-white/5 text-white/90" : "bg-black/5 text-black/80"}`
          }>
            {q.explanation}
          </div>
        )}
      </div>
    );
  };

  if (!Array.isArray(quiz?.questions) || quiz.questions.length === 0) {
    return (
      <div className={
        `w-full rounded-2xl border p-4 ` +
        `${isDarkMode ? "border-white/10 bg-white/5 text-white" : "border-black/10 bg-white text-black"}`
      }>
        {"Invalid quiz payload"}
      </div>
    );
  }

  // Show completion banner when all questions answered (cards still navigable)
  const isComplete = answeredCount === initialQueue.length && initialQueue.length > 0;

  return (
    <div
      className={
        `w-[330px] sm:w-[420px] md:w-[440px] max-w-full rounded-3xl overflow-hidden ` +
        `${isDarkMode ? "bg-black" : "bg-[#f2f3f7]"}`
      }
    >
      <div
        className={
          `px-6 pt-3 pb-2 text-sm font-medium flex items-center gap-3 ` +
          `${isDarkMode ? "text-white" : "text-black"}`
        }
      >
        {/* Circular progress bar */}
        <div className="relative flex-shrink-0" style={{ width: circleSize, height: circleSize }}>
          <svg
            className="-rotate-90"
            width={circleSize}
            height={circleSize}
            viewBox={`0 0 ${circleSize} ${circleSize}`}
          >
            {/* Background circle */}
            <circle
              cx={circleSize / 2}
              cy={circleSize / 2}
              r={circleRadius}
              fill="none"
              stroke={isDarkMode ? "rgba(255,255,255,0.1)" : "#e5e7eb"}
              strokeWidth={circleStrokeWidth}
            />
            {/* Progress arc */}
            <circle
              cx={circleSize / 2}
              cy={circleSize / 2}
              r={circleRadius}
              fill="none"
              stroke="#7c5cff"
              strokeWidth={circleStrokeWidth}
              strokeDasharray={circleCircumference}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              className="transition-all duration-500 ease-out"
            />
          </svg>
          {/* Center text */}
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`text-[11px] font-bold tabular-nums leading-none ${isDarkMode ? "text-white/70" : "text-[#4a4a4a]"}`}>
              {answeredCount}/{initialQueue.length}
            </span>
          </div>
        </div>

        <div className="flex-1" />

        <div
          className={`flex items-center gap-0 border rounded-full ${isDarkMode ? "border-white/15" : "border-black/10"}`}
        >
          <button
            type="button"
            onClick={handlePrev}
            aria-label="Previous"
            className={`h-8 w-8 rounded-full flex items-center justify-center transition-colors ${isDarkMode ? "hover:bg-white/10" : "hover:bg-black/5"}`}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={handleNext}
            aria-label="Next"
            className={`h-8 w-8 rounded-full flex items-center justify-center transition-colors ${isDarkMode ? "hover:bg-white/10" : "hover:bg-black/5"}`}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div
        className={
          `relative px-4 sm:px-6 pb-4 pt-3 flex justify-center w-full ` +
          `${isDarkMode ? "bg-[radial-gradient(900px_700px_at_15%_20%,rgba(122,92,255,0.25),transparent_60%),radial-gradient(850px_650px_at_85%_30%,rgba(0,220,255,0.22),transparent_60%),radial-gradient(1000px_800px_at_50%_85%,rgba(255,71,188,0.18),transparent_60%),linear-gradient(180deg,#050712_0%,#070a12_60%,#050712_100%)]" : "bg-[radial-gradient(900px_700px_at_15%_20%,rgba(122,92,255,0.15),transparent_60%),radial-gradient(850px_650px_at_85%_30%,rgba(0,220,255,0.15),transparent_60%),radial-gradient(1000px_800px_at_50%_85%,rgba(255,71,188,0.12),transparent_60%),linear-gradient(180deg,#f6f8fc_0%,#e9edf5_60%,#f6f8fc_100%)]"}`
        }
        style={{
          perspective: 1200,
          minHeight: stackMinHeight,
        }}
      >
        {visibleCards.map((q, idx) => (
          <Card key={q.id} q={q} idx={idx} />
        ))}
      </div>
    </div>
  );
};
